import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFoldersByWorkspace } from '../../api/folders.api';
import { useCurrentWorkspaceId } from '@/entities/workspace';

/**
 * Hook to fetch folders for the current workspace using TanStack Query
 *
 * @param enabled - Whether the query should be enabled (default: true)
 * @returns TanStack Query result with folders data
 */
export const useFolders = (enabled = true) => {
  const workspaceId = useCurrentWorkspaceId();

  return useQuery({
    queryKey: ['folders', workspaceId],
    queryFn: () => getFoldersByWorkspace(workspaceId ?? ''),
    enabled: enabled && !!workspaceId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch a single folder by ID
 * Uses the cached folders data from useFolders
 *
 * @param folderId - The ID of the folder to fetch
 * @returns The folder data and loading state
 */
export const useFolder = (folderId: string | undefined) => {
  const { data: folders, isPending, isError, error } = useFolders();

  const folder = useMemo(() => {
    if (!folders || !folderId) return null;
    return folders.find((f) => f.id === folderId) ?? null;
  }, [folders, folderId]);

  return {
    data: folder,
    isPending,
    isError,
    error,
    isNotFound: !isPending && !folder && !!folderId,
  };
};
